import { Fragment, useEffect, useRef, useState, memo } from 'react';
import { useReduxState } from 'hooks';
import { scrollToElement } from 'lib/common';
import { useEditorContext } from 'components/editor';
import { EditorBlock } from 'components/editor/blocks';
import { editorSocket } from 'components/editor/socket';
import { EditorBlockAdder } from '.';

interface Props {
  chapterId: number;
  isReady: boolean;
}

const EditorBlocks = ({ chapterId, isReady }: Props) => {
  const {
    editorActiveLanguageVersion,
    editorAdders,
    editorBlocks,
    editorBlocksOrder,
    editorBlockTypes,
    editorCourseLanguage,
    editorCroppingBlockId,
    editorCurrentChapter,
    editorIsBlockMoveView,
    editorPreviewLanguage,
    editorPreviewVersionCourseLanguage,
    editorPreviewType,
    editorTranslationReferenceLanguage
  } = useEditorContext();

  const {
    pageState: { course }
  } = useReduxState();

  const scrollToBlockTimeout = useRef<NodeJS.Timeout>(undefined);

  const [moveToBlockId, setMoveToBlockId] = useState<number>(null);

  // fall back to the course language that arrived with these blocks rather than
  // the redux course, which updates independently on navigation and would let
  // us render a chapter's blocks against a different course's language
  const languageVersion = editorPreviewLanguage
    ? editorPreviewLanguage
    : typeof editorActiveLanguageVersion === 'string'
    ? editorActiveLanguageVersion
    : editorPreviewVersionCourseLanguage
    ? editorPreviewVersionCourseLanguage
    : editorCourseLanguage || course.language;

  useEffect(() => {
    if (moveToBlockId && isReady) {
      scrollToBlock(moveToBlockId);
      setMoveToBlockId(null);
    }
  }, [moveToBlockId, isReady]);

  useEffect(() => {
    if (document?.location?.hash) {
      const blockId = parseInt(document.location.hash.slice(1));
      if (blockId) setMoveToBlockId(blockId);
    }

    return () => clearTimeout(scrollToBlockTimeout.current);
  }, []);

  const handleMoveBlocks = (position: number) => {
    const blockIds = editorBlocksOrder?.filter(
      (blockId: number) => editorBlocks[blockId]?.isSelected
    );

    // find how many of the selected blocks are above the desired position
    // because we need to reduce the position index by that number to account
    // for when the blocks are first removed before being reinserted
    let blocksAbovePosition = 0;
    for (let i = 0; i < position; i++) {
      const blockId = editorBlocksOrder[i];
      if (blockIds.includes(blockId)) blocksAbovePosition++;
    }

    editorSocket.moveBlock({
      blockIds,
      chapterId,
      editorBlocksOrder,
      newPosition: position - blocksAbovePosition
    });
  };

  const scrollToBlock = (blockId: number) => {
    clearTimeout(scrollToBlockTimeout.current);

    scrollToBlockTimeout.current = setTimeout(() => {
      const element = document.querySelector(
        `.block[data-block-id="${blockId}"]`
      ) as HTMLElement;
      if (element) scrollToElement({ element, offset: 200 });
    }, 100);
  };

  const blocksSelected = editorBlocksOrder?.some(
    (blockId: number) => editorBlocks[blockId]?.isSelected
  );

  return !editorBlocksOrder || !editorBlocks ? null : (
    <div className={`blocks ${blocksSelected ? 'blocks-selected' : ''}`}>
      {!editorPreviewType && !editorActiveLanguageVersion && (
        <EditorBlockAdder
          active={editorAdders[0]}
          blockTypes={editorBlockTypes}
          chapterId={chapterId}
          courseId={course.id}
          handleMoveBlocks={handleMoveBlocks}
          isBlockMoveView={editorIsBlockMoveView}
          position={0}
        />
      )}

      {editorBlocksOrder?.map((blockId: number, i: number) => {
        const block = editorBlocks[blockId];

        return !block ? null : (
          <Fragment key={blockId}>
            <EditorBlock
              blockId={blockId}
              chapterId={chapterId}
              data={block.data}
              editor={block.editor}
              isEdited={block.isEdited}
              isNew={block.isNew}
              isSelected={block.isSelected}
              languagesMissingTranslations={block.missingTranslations}
              languageVersion={languageVersion}
              type={block.type}
              updatedAt={block.updatedAt}
              // editor state props
              // we pass these as props to minimize
              // re-renders of individual blocks
              editorChapter={editorCurrentChapter}
              editorBlockIsMoving={editorIsBlockMoveView}
              editorBlockIdForCropping={editorCroppingBlockId}
              editorLanguageActive={editorActiveLanguageVersion}
              editorLanguageReference={editorTranslationReferenceLanguage}
              editorPreviewType={editorPreviewType}
            />
            {!editorPreviewType && !editorActiveLanguageVersion && (
              <EditorBlockAdder
                active={editorAdders[i + 1]}
                blockTypes={editorBlockTypes}
                chapterId={chapterId}
                courseId={course.id}
                handleMoveBlocks={handleMoveBlocks}
                isBlockMoveView={editorIsBlockMoveView}
                position={i + 1}
              />
            )}
          </Fragment>
        );
      })}

      <style jsx>{`
        .blocks-selected :global(.block-select-button) {
          visibility: visible;
        }
      `}</style>
    </div>
  );
};

export default memo(EditorBlocks);
